"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@ui/components/avatar";
import { ScrollArea, ScrollBar } from "@ui/components/scroll-area";
import { useTranslations } from "next-intl";
import { memo, useMemo } from "react";
import { HomeSection } from "./HomeSection";

/** DeeVid 评价卡四色轮换：蓝 / 粉 / 橙 / 绿 */
const CARD_TINTS = ["#e0edff", "#ffebfb", "#ffefdc", "#d7fff0"];

interface TestimonialAuthor {
	name: string;
	handle: string;
	imageUrl: string;
}

interface Testimonial {
	body: string;
	author: TestimonialAuthor;
}

const TestimonialsComponent = memo(function TestimonialsComponent() {
	const t = useTranslations();

	// Helper function to get testimonial data
	const getTestimonial = useMemo(
		() => (path: string) => {
			const handle = t(`home.testimonials.${path}.author.handle`);
			return {
				body: t(`home.testimonials.${path}.body`),
				author: {
					name: t(`home.testimonials.${path}.author.name`),
					handle,
					// DiceBear avataaars 按 handle 做 seed 预生成的 SVG（同 imgveo 风格），
					// 不再用 i18n 里的 Unsplash 真人照，也不在运行时请求第三方
					imageUrl: `/images/avatars/${handle}.svg`,
				},
			};
		},
		[t],
	);

	// Get testimonials data from translations - memoized to avoid re-computation
	const featuredTestimonial = useMemo(
		() => getTestimonial("featured"),
		[getTestimonial],
	);

	// Get testimonials array from translations using named keys - memoized
	const testimonials = useMemo(
		() => [
			[
				[
					getTestimonial("testimonials.col1.row1.item1"),
					getTestimonial("testimonials.col1.row1.item2"),
					getTestimonial("testimonials.col1.row1.item3"),
				],
				[
					getTestimonial("testimonials.col1.row2.item1"),
					getTestimonial("testimonials.col1.row2.item2"),
				],
			],
			[
				[
					getTestimonial("testimonials.col2.row1.item1"),
					getTestimonial("testimonials.col2.row1.item2"),
				],
				[
					getTestimonial("testimonials.col2.row2.item1"),
					getTestimonial("testimonials.col2.row2.item2"),
					getTestimonial("testimonials.col2.row2.item3"),
				],
			],
		],
		[getTestimonial],
	);

	// DeeVid 评价卡一行横排（DESIGN.md §7.6.7）；featured 放第一张，其余按原顺序
	const cards = useMemo(
		() => [featuredTestimonial, ...testimonials.flat(2)],
		[featuredTestimonial, testimonials],
	);

	return (
		<HomeSection title={t("home.testimonials.title")}>
			<ScrollArea className="-mx-4 sm:-mx-6">
				<div className="flex w-max gap-5 px-4 pb-2 sm:px-6">
					{cards.map((testimonial, index) => (
						<figure
							key={`${testimonial.author.handle}-${index}`}
							className="flex w-[280px] flex-col gap-2.5 rounded-20 px-6 py-8 sm:w-[340px] sm:px-[30px] sm:py-11"
							style={{
								backgroundColor:
									CARD_TINTS[index % CARD_TINTS.length],
							}}
						>
							{/* avataaars 是透明底，垫白圆才不会和彩色卡面混在一起 */}
							<Avatar className="mb-2 size-20 bg-white ring-2 ring-white">
								<AvatarImage
									src={testimonial.author.imageUrl}
									alt=""
								/>
								<AvatarFallback className="bg-white text-[#5c5c5c] text-lg">
									{testimonial.author.name.slice(0, 1)}
								</AvatarFallback>
							</Avatar>
							<blockquote className="flex-1 text-[#5c5c5c] text-sm leading-[18px]">
								<p>{`"${testimonial.body}"`}</p>
							</blockquote>
							<figcaption className="mt-2">
								<div className="font-bold text-[#5c5c5c] text-xl">
									{testimonial.author.name}
								</div>
								<div className="font-medium text-[#5c5c5c] text-sm">{`@${testimonial.author.handle}`}</div>
							</figcaption>
						</figure>
					))}
				</div>
				<ScrollBar orientation="horizontal" className="hidden" />
			</ScrollArea>
		</HomeSection>
	);
});

TestimonialsComponent.displayName = "Testimonials";

export { TestimonialsComponent as Testimonials };
